import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';

interface PackageSizeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PackageSizeModal: React.FC<PackageSizeModalProps> = ({ isOpen, onClose }) => {
  const packageOptions = [
    {
      size: '½ Gallon',
      description: 'Perfect for small repairs and touch-ups',
      stripeUrl: 'https://buy.stripe.com/14A6oHeCf8uw3D3ctm9oc01',
      icon: '📦'
    },
    {
      size: '1 Gallon',
      description: 'Ideal for medium-sized projects',
      stripeUrl: 'https://buy.stripe.com/14A9AT51F2680qR2SM9oc03',
      icon: '📦'
    },
    {
      size: '5 Gallon Pail',
      description: 'Best for large applications',
      stripeUrl: 'https://buy.stripe.com/9B63cv9hVaCE7Tjctm9oc02',
      icon: '🪣'
    }
  ];

  const handleBuyNow = (stripeUrl: string) => {
    window.open(stripeUrl, '_blank');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-2xl font-bold text-gray-900">Choose Package Size</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-600 mb-6 text-center">
                  Select the package size that best fits your project needs
                </p>

                {/* Package Options */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {packageOptions.map((option, index) => (
                    <motion.div
                      key={option.size}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-2 border-gray-200 rounded-lg p-6 hover:border-primary-500 transition-colors cursor-pointer"
                      onClick={() => handleBuyNow(option.stripeUrl)}
                    >
                      <div className="text-center">
                        <div className="text-4xl mb-3">{option.icon}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{option.size}</h3>
                        <p className="text-sm text-gray-600 mb-4">{option.description}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBuyNow(option.stripeUrl);
                          }}
                          className="w-full btn-primary flex items-center justify-center gap-2"
                        >
                          <ShoppingCartIcon className="w-5 h-5" />
                          Buy Now
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Additional Info */}
                <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-blue-800 text-center">
                    <strong>Note:</strong> All packages include Flexcrete liquid and mixing instructions. 
                    Type M Mortar mix is sold separately.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PackageSizeModal;
